// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * main.ts — bootstrap and screen flow.
 *
 * Owns no game rules (they are in game.ts) and no netcode protocol (that is the
 * engine's). Its job is: mount the DOM, wire input to `Match`, and move between
 * menu → mode → [solo | room entry → lobby] → countdown → game → results.
 *
 * The one thing worth reading carefully is the room lifecycle. ONE Net per
 * session, joined once and held until the player goes back to the menu; every
 * rematch happens INSIDE it via rematch.ts. Never leave and rejoin to reset —
 * see the engine README for why that is fatal rather than merely wasteful.
 */

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, type Net, type PeerId, roomAppId, setTurnConfig } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type Rounds } from '@ben-gy/game-engine/rematch';
import {
  clearRoomInUrl,
  createLobby,
  createRoomEntry,
  normalizeRoomCode,
  setRoomInUrl,
} from '@ben-gy/game-engine/lobby';

import { chooseAction, type Strength } from './ai';
import {
  type Action,
  convertTargets,
  destinationsFor,
  type GameState,
  legalActions,
  placementsFor,
  polarityAt,
  type Seat,
  type WireAction,
} from './game';
import { type Hex, key } from './hex';
import { createOneShot, Match, seatOf } from './match';
import { DEFAULT_MODE_ID, MODE_IDS, modeOf, type PieceType } from './modes';
import { PALETTE, SEAT_NAME } from './palette';
import {
  computeLayout,
  draw,
  hexAt,
  hexCentre,
  type Layout,
  spawnBurst,
  stepParticles,
  type ViewState,
} from './render';
import { runCountdown } from './countdown';
import { aboutSheetHtml, helpSheetHtml, renderHand, resultsHtml, type ResultRow } from './ui';

const SLUG = 'rootbound';
const store = createStore(SLUG);
const sfx = createSfx(store.get<boolean>('muted', false) ?? false);

const reducedMotion =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const app = document.getElementById('app')!;
let content: HTMLElement;
let footer: HTMLElement;

/* ── session-wide state ─────────────────────────────────────────────────── */

let net: Net | null = null;
let rounds: Rounds | null = null;
let match: Match | null = null;
let roomCode = '';
let hostOptsMode = DEFAULT_MODE_ID;
let myModeChoice = (store.get<string>('mode', DEFAULT_MODE_ID) ?? DEFAULT_MODE_ID) as string;
let strength = (store.get<number>('strength', 2) ?? 2) as Strength;
/** Running match tally across rematches, indexed by seat. */
let tally: [number, number] = [0, 0];
let roster: Array<{ id: PeerId; name: string }> = [];
let playerName = store.get<string>('name', '') || `Player ${Math.floor(Math.random() * 900 + 100)}`;
store.set('name', playerName);

/** Torn down on every screen change so nothing leaks across screens. */
let cleanups: Array<() => void> = [];
function onCleanup(fn: () => void): void {
  cleanups.push(fn);
}
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}

/* ── shell ──────────────────────────────────────────────────────────────── */

function mountShell(): void {
  app.innerHTML = `
    <main class="main-content" id="content"></main>
    <footer class="site-footer">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://hub.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
    </footer>`;
  content = document.getElementById('content')!;
  footer = app.querySelector('.site-footer')!;
  void footer;
}

function setPlaying(on: boolean): void {
  document.body.classList.toggle('playing', on);
}

function sheet(html: string, onClose?: () => void): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = html;
  const close = (): void => {
    wrap.remove();
    document.removeEventListener('keydown', esc);
    onClose?.();
  };
  const esc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  wrap.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
  wrap.addEventListener('click', (e) => {
    if (e.target === wrap) close();
  });
  document.addEventListener('keydown', esc);
  document.body.appendChild(wrap);
  wrap.querySelector<HTMLElement>('button')?.focus();
}

/* ── menu ───────────────────────────────────────────────────────────────── */

function showMenu(): void {
  teardown();
  setPlaying(false);
  // A room is a choice, not a destiny: drop ?room= on the way out so a reload
  // (or reopening from a home-screen icon) never drags the player back in.
  clearRoomInUrl();
  content.innerHTML = `
    <section class="screen">
      <h1 class="title">Rootbound</h1>
      <p class="tagline">There is no board — only pieces, and they all touch.
        Ring your rival's Heartroot without splitting the colony.</p>
      <div class="menu-actions">
        <button class="btn primary" id="solo">Play solo</button>
        <button class="btn" id="friends">Play with a friend</button>
        <div class="menu-row">
          <button class="btn ghost" id="help">How to play</button>
          <button class="btn ghost" id="about">About</button>
        </div>
        <button class="btn ghost" id="mute">${sfx.muted() ? '🔇 Sound off' : '🔊 Sound on'}</button>
      </div>
    </section>`;

  const on = (id: string, fn: () => void): void => {
    content.querySelector(`#${id}`)!.addEventListener('click', () => {
      unlockAudio();
      fn();
    });
  };
  on('solo', showSoloSetup);
  on('friends', showRoomEntry);
  on('help', () => sheet(helpSheetHtml()));
  on('about', () => sheet(aboutSheetHtml()));
  on('mute', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpSheetHtml());
  }
}

let audioUnlocked = false;
function unlockAudio(): void {
  if (audioUnlocked) return;
  audioUnlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio is a nicety; never let it break a tap */
  }
}

/* ── mode picker (shared by solo and the lobby) ─────────────────────────── */

function modePicker(selected: string, onPick: (id: string) => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'modes';
  for (const id of MODE_IDS) {
    const m = modeOf(id);
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn mode-btn';
    b.setAttribute('aria-pressed', String(id === selected));
    b.innerHTML = `<span class="mode-name">${m.name}</span><span class="mode-blurb">${m.blurb}</span>`;
    b.addEventListener('click', () => onPick(id));
    wrap.appendChild(b);
  }
  return wrap;
}

function showSoloSetup(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = `
    <section class="screen">
      <h2 class="re-title">Play solo</h2>
      <p class="re-sub">Pick a shape of round, then an opponent.</p>
      <div id="modes"></div>
      <div class="menu-actions">
        <div class="menu-row" id="strengths"></div>
        <button class="btn primary" id="go">Start</button>
        <button class="btn ghost" id="back">Back</button>
      </div>
    </section>`;

  const modesEl = content.querySelector('#modes')!;
  const paint = (): void => {
    modesEl.innerHTML = '';
    modesEl.appendChild(
      modePicker(myModeChoice, (id) => {
        myModeChoice = id;
        store.set('mode', id);
        paint();
      }),
    );
  };
  paint();

  const sEl = content.querySelector('#strengths')!;
  const paintStrength = (): void => {
    sEl.innerHTML = '';
    for (const [label, val] of [
      ['Gentle', 1],
      ['Steady', 2],
      ['Fierce', 3],
    ] as Array<[string, Strength]>) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'btn';
      b.textContent = label;
      b.setAttribute('aria-pressed', String(val === strength));
      b.addEventListener('click', () => {
        strength = val;
        store.set('strength', val);
        paintStrength();
      });
      sEl.appendChild(b);
    }
  };
  paintStrength();

  content.querySelector('#go')!.addEventListener('click', () => startSolo());
  content.querySelector('#back')!.addEventListener('click', showMenu);
}

/* ── the game screen ────────────────────────────────────────────────────── */

interface GameScreen {
  view: ViewState;
  repaint: () => void;
  destroy: () => void;
}

function buildGameScreen(
  m: Match,
  opts: { onQuit: () => void; label: () => string },
): GameScreen {
  content.innerHTML = `
    <div class="game-wrap">
      <div class="hud">
        <div class="turn-chip" id="turn"></div>
        <span class="clock" id="clock"></span>
        <button class="hud-btn" id="help" aria-label="How to play">?</button>
        <button class="hud-btn" id="quit" aria-label="Leave the round">✕</button>
      </div>
      <div class="stage" id="stage"><canvas id="board"></canvas></div>
      <p class="hint" id="hint"></p>
      <div class="hand" id="hand"></div>
    </div>`;

  const stage = content.querySelector<HTMLElement>('#stage')!;
  const canvas = content.querySelector<HTMLCanvasElement>('#board')!;
  const ctx = canvas.getContext('2d')!;
  const handEl = content.querySelector<HTMLElement>('#hand')!;
  const turnEl = content.querySelector<HTMLElement>('#turn')!;
  const clockEl = content.querySelector<HTMLElement>('#clock')!;
  const hintEl = content.querySelector<HTMLElement>('#hint')!;

  const view: ViewState = {
    game: m.state,
    seat: m.selfSeat,
    selected: null,
    targets: [],
    held: null,
    convertChoices: [],
    anim: null,
    particles: [],
    motion: !reducedMotion,
    shake: 0,
  };

  /**
   * The layout, recomputed whenever the board or the viewport changes — and
   * NEVER only inside the draw call.
   *
   * Sporeline computed its cell size inside the rAF draw, so `cellAt` returned
   * nothing before the first frame and every tap was silently dropped: no
   * error, no toast, a normal-looking board. It hid from automation because any
   * check that screenshots first fires a frame and makes the bug vanish.
   */
  let layout: Layout = { size: 20, ox: 0, oy: 0, w: 0, h: 0 };

  function relayout(): void {
    const rect = stage.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (!(w > 0) || !(h > 0)) return; // transient 0-size measure; retry next frame
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    layout = computeLayout(view.game.cells.keys(), [...view.targets, ...view.convertChoices], w, h);
  }

  function repaint(): void {
    view.game = m.state;
    relayout();
    draw(ctx, layout, view);
    renderHand(handEl, m.state, m.selfSeat, view.held, m.myTurn && !m.state.over, pickHand);
    turnEl.innerHTML = opts.label();
    const secs = Math.ceil(m.msLeft / 1000);
    clockEl.textContent = m.state.over ? '' : `${secs}s`;
    clockEl.classList.toggle('urgent', secs <= 10);
    updateHint();
  }

  function updateHint(): void {
    hintEl.className = 'hint';
    if (m.state.over) {
      hintEl.textContent = '';
      return;
    }
    const abandon = m.abandonMsLeft;
    if (abandon !== null) {
      hintEl.textContent = `Opponent disconnected — forfeits in ${Math.ceil(abandon / 1000)}s`;
      return;
    }
    if (view.convertChoices.length) {
      hintEl.textContent = 'Choose an enemy piece for the spores to take.';
      return;
    }
    if (!m.myTurn) {
      hintEl.textContent = m.selfSeat === null ? 'Spectating' : "Opponent's turn…";
      return;
    }
    if (view.selected) {
      const p = m.state.cells.get(key(view.selected));
      if (p) {
        const pol = polarityAt(m.state, view.selected, p.owner);
        hintEl.className = `hint polarity-${pol}`;
        hintEl.textContent =
          pol === 'nested'
            ? 'Nested — among friends, so it moves one cell.'
            : 'Reaching — outnumbered, so it moves its full range.';
        return;
      }
    }
    if (view.held) {
      hintEl.textContent = 'Tap a highlighted cell to place.';
      return;
    }
    hintEl.textContent =
      m.state.hands[m.selfSeat ?? 0].heartroot > 0
        ? 'Place your Heartroot to begin.'
        : 'Tap a piece to move it, or a hand tile to place.';
  }

  /* ── input ───────────────────────────────────────────────────────────── */

  function clearSelection(): void {
    view.selected = null;
    view.targets = [];
    view.held = null;
    view.convertChoices = [];
  }

  function pickHand(p: PieceType): void {
    unlockAudio();
    if (!m.myTurn) return;
    if (view.held === p) {
      clearSelection();
    } else {
      clearSelection();
      view.held = p;
      view.targets = placementsFor(m.state, p);
      sfx.play('select');
    }
    repaint();
  }

  let pendingSpore: Hex | null = null;

  function commit(a: Action): void {
    const before = m.state;
    if (!m.play(a)) return;
    afterAction(before, a);
  }

  function afterAction(before: GameState, a: Action): void {
    clearSelection();
    pendingSpore = null;
    if (a.k === 'move') {
      const piece = before.cells.get(key(a.from));
      if (piece) view.anim = { from: a.from, to: a.to, piece, t: 0 };
      sfx.play('jump');
    } else if (a.k === 'place') {
      sfx.play(a.convert ? 'powerup' : 'coin');
      const c = hexCentre(layout, a.to);
      spawnBurst(view.particles, c.x, c.y, PALETTE.target, a.convert ? 18 : 10);
      if (a.convert) {
        const cc = hexCentre(layout, a.convert);
        spawnBurst(view.particles, cc.x, cc.y, PALETTE.reaching, 16);
        view.shake = view.motion ? 5 : 0;
      }
    }
    repaint();
  }

  function tapCell(hx: Hex): void {
    unlockAudio();
    if (m.state.over || !m.myTurn) return;

    // Second half of a Sporeling placement: which enemy do the spores take?
    if (pendingSpore && view.convertChoices.some((c) => c.q === hx.q && c.r === hx.r)) {
      commit({ k: 'place', piece: 'sporeling', to: pendingSpore, convert: hx });
      return;
    }

    if (view.held) {
      if (!view.targets.some((t) => t.q === hx.q && t.r === hx.r)) {
        clearSelection();
        repaint();
        return;
      }
      if (view.held === 'sporeling') {
        const choices = convertTargets(m.state, hx);
        if (choices.length > 1) {
          pendingSpore = hx;
          view.targets = [];
          view.convertChoices = choices;
          sfx.play('select');
          repaint();
          return;
        }
        commit({ k: 'place', piece: 'sporeling', to: hx, convert: choices[0] });
        return;
      }
      commit({ k: 'place', piece: view.held, to: hx });
      return;
    }

    if (view.selected && view.targets.some((t) => t.q === hx.q && t.r === hx.r)) {
      commit({ k: 'move', from: view.selected, to: hx });
      return;
    }

    const piece = m.state.cells.get(key(hx));
    if (piece && piece.owner === m.selfSeat) {
      const dests = destinationsFor(m.state, hx);
      clearSelection();
      view.selected = hx;
      view.targets = dests;
      sfx.play(dests.length ? 'select' : 'blip');
    } else {
      clearSelection();
    }
    repaint();
  }

  // Pointer Events only, with capture and a real pointercancel path.
  let down: { id: number; x: number; y: number } | null = null;

  const onDown = (e: PointerEvent): void => {
    canvas.setPointerCapture(e.pointerId);
    down = { id: e.pointerId, x: e.clientX, y: e.clientY };
  };
  const onUp = (e: PointerEvent): void => {
    if (!down || down.id !== e.pointerId) return;
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    down = null;
    // A drag ending on a cell plays it, exactly as a tap does — but a scroll-ish
    // flick with no target under it should not fire.
    const rect = canvas.getBoundingClientRect();
    // Read from clientX/Y minus the rect, never offsetX/Y, which scales oddly
    // under devicePixelRatio and browser zoom.
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    void moved;
    tapCell(hexAt(layout, x, y));
  };
  const onCancel = (): void => {
    down = null;
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onCancel);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      clearSelection();
      repaint();
    } else if (e.key === '?' || e.key === 'h') {
      sheet(helpSheetHtml());
    }
  };
  document.addEventListener('keydown', onKey);

  const onResize = (): void => repaint();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  content.querySelector('#help')!.addEventListener('click', () => sheet(helpSheetHtml()));
  content.querySelector('#quit')!.addEventListener('click', opts.onQuit);

  /* ── frames + clock ──────────────────────────────────────────────────── */

  let raf = 0;
  let last = performance.now();
  const frame = (t: number): void => {
    const dt = Math.min(0.05, (t - last) / 1000);
    last = t;
    if (view.anim) {
      view.anim.t += dt * 4.2;
      if (view.anim.t >= 1) view.anim = null;
    }
    if (view.particles.length) stepParticles(view.particles, dt);
    if (view.shake > 0) view.shake = Math.max(0, view.shake - dt * 22);
    draw(ctx, layout, view);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  // The clock runs on an interval, NOT on rAF: a backgrounded tab stops firing
  // rAF, which would freeze the authoritative clock precisely when the host
  // tabs away.
  const CLOCK_MS = 500;
  const clockTimer = setInterval(() => {
    m.tick(CLOCK_MS);
    if (!m.state.over) {
      const secs = Math.ceil(m.msLeft / 1000);
      clockEl.textContent = `${secs}s`;
      clockEl.classList.toggle('urgent', secs <= 10);
      if (m.abandonMsLeft !== null) updateHint();
    }
  }, CLOCK_MS);

  // The opening board is empty, and an empty board with no highlighted cell
  // reads as "nothing has loaded". The Heartroot is the ONLY legal thing to
  // play on turn one, so pick it up automatically: the player sees where they
  // may put it instead of an unexplained void.
  if (m.myTurn && m.selfSeat !== null && m.state.hands[m.selfSeat].heartroot > 0) {
    view.held = 'heartroot';
    view.targets = placementsFor(m.state, 'heartroot');
  }

  // A first layout pass BEFORE any frame, so a tap that lands before the first
  // rAF still hits the right cell.
  relayout();
  repaint();

  return {
    view,
    repaint,
    destroy() {
      cancelAnimationFrame(raf);
      clearInterval(clockTimer);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    },
  };
}

/* ── solo ───────────────────────────────────────────────────────────────── */

function startSolo(): void {
  teardown();
  setPlaying(true);
  const mode = modeOf(myModeChoice);
  const seed = Math.floor(Math.random() * 1e9);

  const m = new Match({
    mode,
    selfSeat: 0,
    isHost: true,
    seed,
    deps: {
      send: () => {},
      onChange: () => {},
      onEnd: (g) => {
        setTimeout(() => showResults(g, null), 700);
      },
    },
  });
  match = m;

  let screen: GameScreen;

  // A poke-safe one-shot, NOT `clearTimeout` + `setTimeout`. See createOneShot:
  // re-arming on every poll tick starves the timer and the bot never moves.
  const botShot = createOneShot(420, () => {
    if (m.state.over || m.state.toMove === 0) return;
    const before = m.state;
    const a = chooseAction(m.state, { strength, seed: `${seed}:bot` });
    // The bot goes through the same validated path a peer's move would.
    m.receive(encode(a), 1);
    screen.view.game = m.state;
    if (a.k === 'move') {
      const piece = before.cells.get(key(a.from));
      if (piece) screen.view.anim = { from: a.from, to: a.to, piece, t: 0 };
      sfx.play('jump');
    } else {
      sfx.play(a.k === 'place' && a.convert ? 'powerup' : 'coin');
    }
    screen.repaint();
  });

  const botMaybe = (): void => {
    if (m.state.over || m.state.toMove === 0) return;
    botShot.poke();
  };

  screen = buildGameScreen(m, {
    onQuit: () => {
      screen.destroy();
      showMenu();
    },
    label: () =>
      m.state.over
        ? 'Round over'
        : m.state.toMove === 0
          ? '<span class="seat-dot s0"></span> Your turn'
          : '<span class="seat-dot s1"></span> Thinking…',
  });
  onCleanup(() => {
    botShot.cancel();
    screen.destroy();
  });

  // Poll for the bot's turn on an interval so a backgrounded tab still resolves.
  const poll = setInterval(botMaybe, 300);
  onCleanup(() => clearInterval(poll));

  runCountdown(document.body, sfx, () => {});
}

function encode(a: Action): WireAction {
  // Re-exported through game.ts; kept local so main.ts's imports stay small.
  if (a.k === 'pass') return [0];
  if (a.k === 'place') {
    const order: PieceType[] = [
      'heartroot',
      'creeper',
      'burrower',
      'spindle',
      'warden',
      'sporeling',
    ];
    return [
      1,
      order.indexOf(a.piece),
      a.to.q,
      a.to.r,
      a.convert ? a.convert.q : 0x7fff,
      a.convert ? a.convert.r : 0x7fff,
    ];
  }
  return [2, a.from.q, a.from.r, a.to.q, a.to.r];
}

/* ── multiplayer ────────────────────────────────────────────────────────── */

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen" id="entry"></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  // A deep-linked invite goes straight in, but is consumed ONCE: the next
  // "Play with a friend" shows this screen again rather than teleporting the
  // player back into a room they already left.
  const url = new URL(location.href);
  const deep = url.searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    clearRoomInUrl();
    if (code.length >= 3) {
      enterRoom(code, false);
      return;
    }
  }

  createRoomEntry({
    container: host,
    onSubmit: (code, created) => enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with a friend',
    subtitle: 'Start a room and share the code, or type the code your friend sent you.',
  });
}

/**
 * Join the room ONCE, and hold it for the whole session.
 *
 * Everything after this — lobby, round, results, rematch, another round — lives
 * inside this single Net. `net.leave()` is called only on the way back to the
 * menu. Leaving and rejoining to "reset" hands back a dying room object and
 * every peer then elects itself host; the engine throws if you try.
 */
async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [0, 0];

  content.innerHTML = `
    <section class="screen">
      <div class="lobby-searching"><span class="spinner"></span> Connecting…</div>
    </section>`;

  // Must run before the FIRST mesh on the page: Trystero builds one global
  // connection pool from the first joinRoom's config, so a later call leaves
  // half of every pair STUN-only. Session-cached and fail-open.
  try {
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      onHostChange: (_id, isSelfHost) => {
        match?.setHost(isSelfHost);
        repaintLobbyIfPresent();
      },
      onPeers: () => repaintLobbyIfPresent(),
      onPeerLeave: (id) => {
        const seat = seatOf(roster, id);
        if (seat !== null) match?.peerLeft(seat);
        repaintLobbyIfPresent();
      },
      onPeerJoin: () => match?.peerReturned(),
    },
  );
  net = n;

  const r = createRounds({
    net: n,
    playerName,
    minPlayers: 2,
    // The HOST's pick is what the room plays, frozen into the start. A setting
    // each peer reads from its own UI is a setting two peers can disagree
    // about — and here that would mean two different rule sets on one board.
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      // Render the host's GOSSIPED choice, never our own local pick labelled as
      // theirs — that is a confident lie and it has shipped before.
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobbyIfPresent();
    },
  });
  rounds = r;

  // Move data channel. Lockstep: this carries an Action and nothing else.
  const sendMove = n.channel<WireAction>('mv', (data, from) => {
    if (!match) return;
    const seat = seatOf(roster, from);
    if (seat === null) return;
    const before = match.state;
    if (match.receive(data, seat)) {
      const a = decodeForAnim(before, data);
      currentScreen?.repaint();
      if (a) animateRemote(before, a);
    }
  });
  netSend = (w) => sendMove(w);

  onCleanup(() => {
    r.destroy();
    sendMove.off();
  });

  showLobby();
}

let netSend: (w: WireAction) => void = () => {};
let currentScreen: GameScreen | null = null;
let lobbyMounted = false;

function decodeForAnim(before: GameState, w: unknown): Action | null {
  const legal = legalActions(before);
  const nums = Array.isArray(w) ? (w as number[]) : null;
  if (!nums) return null;
  if (nums[0] === 2) {
    return (
      legal.find(
        (a) =>
          a.k === 'move' &&
          a.from.q === nums[1] &&
          a.from.r === nums[2] &&
          a.to.q === nums[3] &&
          a.to.r === nums[4],
      ) ?? null
    );
  }
  return null;
}

function animateRemote(before: GameState, a: Action): void {
  if (!currentScreen || a.k !== 'move') return;
  const piece = before.cells.get(key(a.from));
  if (piece) currentScreen.view.anim = { from: a.from, to: a.to, piece, t: 0 };
  sfx.play('jump');
}

function repaintLobbyIfPresent(): void {
  // A net handler can fire in the LOBBY or moments after a round ends. It must
  // never navigate: a showMenu() fallback here ejected both peers in one game,
  // and a late repaint painted over another's results screen.
  if (lobbyMounted) showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = `
    <section class="screen">
      <div id="lobby-host"></div>
      <div class="modes" id="lobby-modes"></div>
      <p class="host-pick" id="host-pick"></p>
    </section>`;

  createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: 2,
    onCancel: () => leaveRoom(),
  });

  const isHost = n.isHost() && n.hostSettled();
  const modesEl = content.querySelector<HTMLElement>('#lobby-modes')!;
  const pickEl = content.querySelector<HTMLElement>('#host-pick')!;

  if (isHost) {
    modesEl.appendChild(
      modePicker(myModeChoice, (id) => {
        myModeChoice = id;
        store.set('mode', id);
        showLobby();
      }),
    );
    pickEl.textContent = 'Your pick is what the room plays.';
  } else {
    modesEl.innerHTML = '';
    pickEl.textContent = n.hostSettled()
      ? `The host has chosen ${modeOf(hostOptsMode).name}.`
      : 'Waiting for the host…';
  }
}

function startNetRound(info: RoundInfo): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  const opts = info.opts as { mode?: string } | null;
  const mode = modeOf(opts?.mode);
  const selfSeat = info.seated ? seatOf(roster, net?.selfId ?? '') : null;

  const m = new Match({
    mode,
    selfSeat,
    isHost: info.isHost,
    seed: info.seed,
    deps: {
      send: (w) => netSend(w),
      onChange: () => {},
      onEnd: (g) => {
        if (g.winner !== null) tally[g.winner]++;
        setTimeout(() => showResults(g, roster), 700);
      },
    },
  });
  match = m;
  setPlaying(true);

  const screen = buildGameScreen(m, {
    onQuit: () => backToLobby(),
    label: () => {
      if (m.state.over) return 'Round over';
      if (selfSeat === null) return 'Spectating this round';
      const mine = m.state.toMove === selfSeat;
      return `<span class="seat-dot s${m.state.toMove}"></span> ${
        mine ? 'Your turn' : `${roster[m.state.toMove]?.name ?? SEAT_NAME[m.state.toMove]}'s turn`
      }`;
    },
  });
  currentScreen = screen;
  onCleanup(() => {
    currentScreen = null;
    screen.destroy();
  });

  const cd = runCountdown(document.body, sfx, () => {});
  onCleanup(() => cd.cancel());
}

/* ── results ────────────────────────────────────────────────────────────── */

function showResults(game: GameState, players: Array<{ id: PeerId; name: string }> | null): void {
  teardown();
  setPlaying(false);
  const selfId = net?.selfId ?? '';
  const rows: ResultRow[] = players
    ? players.slice(0, 2).map((p, i) => ({ seat: i as Seat, name: p.name, isSelf: p.id === selfId }))
    : [
        { seat: 0, name: 'You', isSelf: true },
        { seat: 1, name: 'Opponent', isSelf: false },
      ];

  sfx.play(game.winner === null ? 'blip' : rows.find((r) => r.isSelf)?.seat === game.winner ? 'win' : 'lose');

  content.innerHTML = `
    <section class="screen">
      ${resultsHtml(game, rows, tally)}
      <p class="waiting-note" id="waiting"></p>
      <div class="menu-actions">
        <button class="btn primary" id="again">Play again</button>
        <button class="btn ghost" id="lobby"${players ? '' : ' hidden'}>Back to lobby</button>
        <button class="btn ghost" id="menu">Main menu</button>
        <button class="btn ghost" id="feedback">Report a problem</button>
      </div>
    </section>`;

  content.querySelector('#feedback')!.addEventListener('click', (e) =>
    (window as any).feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  content.querySelector('#menu')!.addEventListener('click', () => leaveRoom());

  if (!players) {
    content.querySelector('#again')!.addEventListener('click', () => startSolo());
    return;
  }

  // Multiplayer: "Play again" is a VOTE inside the living room, never a rejoin.
  const r = rounds;
  const waiting = content.querySelector<HTMLElement>('#waiting')!;
  const again = content.querySelector<HTMLButtonElement>('#again')!;

  content.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
  again.addEventListener('click', () => {
    r?.vote();
    again.disabled = true;
    again.textContent = 'Waiting for the others…';
  });

  // A silent wait is indistinguishable from a hang, so show what we are waiting
  // for AND when it ends.
  const paint = (): void => {
    if (!r) return;
    const s = r.state();
    const n = s.votes.length;
    const total = Math.max(s.present.length, 1);
    if (s.startsInMs !== null) {
      waiting.textContent = `Starting in ${Math.ceil(s.startsInMs / 1000)}s — ${n}/${total} ready`;
    } else if (n > 0) {
      waiting.textContent = `${n} of ${total} ready for another round`;
    } else {
      waiting.textContent = '';
    }
  };
  paint();
  const t = setInterval(paint, 400);
  onCleanup(() => clearInterval(t));
}

function backToLobby(): void {
  // Explicitly does NOT leave the room.
  teardown();
  match = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  lobbyMounted = false;
  match = null;
  const n = net;
  net = null;
  rounds = null;
  netSend = () => {};
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort; the menu must appear regardless */
    }
  }
  showMenu();
}

/* ── boot ───────────────────────────────────────────────────────────────── */

function boot(): void {
  hardenViewport();
  mountShell();
  showMenu();

  if (new URL(location.href).searchParams.get('netdebug') === '1') mountNetDebug();

  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

function mountNetDebug(): void {
  const el = document.createElement('div');
  el.className = 'netdebug';
  document.body.appendChild(el);
  setInterval(() => {
    if (!net) {
      el.textContent = 'no room';
      return;
    }
    const d = net.netDiag();
    el.textContent = [
      `self ${d.selfId.slice(0, 6)}`,
      `host ${d.host ? d.host.slice(0, 6) : '—'} e${d.epoch} ${d.settled ? 'settled' : 'unsettled'}`,
      `peers ${d.peers.length} turn ${d.turn ? 'yes' : 'NO'}`,
      `seat ${match?.selfSeat ?? '—'} ply ${match?.state.ply ?? '—'}`,
    ].join('\n');
  }, 500);
}

boot();
